<!--
  QaReport.vue — Care Management > QA > Report: call-quality scorecards.

  THREE LEVELS, master/detail, left-to-right:

    1. CARE MANAGERS   a list — one row per person, ordered by the sort
                       dropdown (highest score first by default)
    2. THEIR CALLS     a table — one row per graded call
    3. ONE CALL        the full rubric: every question, answer, and the
                       verbatim quote the grade was based on

  Scores come from the engagement_qa SAT executor, which grades a call
  transcript against the rubric its TASK TYPE selects. Read-only — nothing here
  actions anything; an auto-fail is shown to a human with its evidence.

  IT IS A SAMPLE AND SAYS SO. Only calls WITH a transcript can be graded (~20%
  of engagements, varying by call type), so every level shows its count and
  flags a small sample. A manager must never read a 3-call average as a verdict.
-->
<template>
  <div style="display: flex; flex-direction: column; flex: 1; min-height: 0;">
    <div style="display: flex; align-items: flex-start; gap: 16px; padding: 0 16px 6px;">
    <div style="font-size: 0.86em; color: #A5A5A6; max-width: 900px;">
      Care managers scored against the QA rubric their call type selects. Only calls with a
      transcript can be graded, so these are <em>samples</em> — a score built on a handful of
      calls is marked <span style="color:#B45309; font-weight:600;">small</span> and should be
      read as a prompt to listen, not as a verdict.
    </div>
      <span v-if="!loading && results.length" style="margin-left: auto; color: #6B7280; font-variant-numeric: tabular-nums; white-space: nowrap;" data-test="report-counts">
        {{ scopedResults.length }} care managers ·
        <span style="font-weight: 600; color: #353537;">{{ totalGraded.toLocaleString() }}</span> graded calls
        <span v-if="totalFlagged" style="color: #B91C1C; font-weight: 600;"> · {{ totalFlagged }} flagged</span>
      </span>
    </div>

    <div style="display: flex; gap: 12px; padding: 6px 16px 0;">
      <FilterDropdown v-if="!reportFiltersReady || rubrics.length > 1" class="w-56" full-width label="Rubric" :loading="!reportFiltersReady" :model-value="reportRubricKey || null" :options="rubricOptions" placeholder="All rubrics" clearable data-test="report-rubric-filter" @update:model-value="setReportRubric" />
      <FilterDropdown class="w-64" full-width :label="opportunityNoun" :model-value="reportProgram || null" :options="programOptions" :placeholder="`All ${opportunityNounPlural.toLowerCase()}`" clearable searchable :loading="!reportFiltersReady" data-test="report-program-filter" @update:model-value="setReportProgram" />
      <template v-if="canAudit">
        <FilterDropdown v-if="!reportFiltersReady || teams.length" class="w-56" full-width label="Team" :model-value="reportTeam" :options="teamOptions" placeholder="All teams" clearable searchable :loading="!reportFiltersReady" data-test="report-team-filter" @update:model-value="reportTeam = $event" />
        <FilterDropdown class="w-56" full-width label="Care manager" :model-value="reportCm" :options="reportCmOptions" placeholder="All care managers" clearable searchable sensitive-labels :loading="!reportFiltersReady" data-test="report-cm-filter" @update:model-value="reportCm = $event" />
      </template>
      <div style="display:flex; align-items:flex-end; gap:4px; margin-left:auto;">
        <button type="button" :disabled="!hasReportFilters" :class="['px-2 py-1.5 text-xs whitespace-nowrap', hasReportFilters ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 cursor-not-allowed']" data-test="report-clear-filters" @click="clearReportFilters"><FontAwesomeIcon :icon="['fas', 'times']" class="mr-1" />Clear Filters</button>
        <button type="button" :disabled="!isReportFilterDirty" :class="['inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors whitespace-nowrap', isReportFilterDirty ? 'border-primary-500 bg-primary-500 text-white hover:opacity-90' : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed']" data-test="report-save-filters" @click="saveReportFilters"><FontAwesomeIcon :icon="['fas', 'floppy-disk']" class="text-[10px]" />Save Filters</button>
        <button type="button" :disabled="loading" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed" data-test="report-refresh" @click="load"><FontAwesomeIcon :icon="['fas', 'arrows-rotate']" class="text-[10px]" :class="{ 'animate-spin': loading }" />Refresh</button>
      </div>
    </div>
    <p v-if="!reportLoading && reportRubricKey && !report.audit_count" style="color:#6B7280;padding: 6px 16px 10px;" data-test="report-empty">No grades this month for this rubric.</p>
    <!-- The stats come from their own (slower) request, so they have their own skeleton. -->
    <div v-if="reportLoading" style="display:flex; gap:8px; padding: 6px 16px 10px; flex-wrap:wrap;" data-test="report-stats-skeleton">
      <div v-for="n in 4" :key="n" class="cm-stat"><div class="h-2.5 w-20 bg-gray-200 rounded animate-pulse mb-2" /><div class="h-5 w-14 bg-gray-200 rounded animate-pulse" /></div>
    </div>
    <div v-else-if="report.audit_count" style="display:flex; gap:8px; padding: 6px 16px 10px; flex-wrap:wrap;">
      <div class="cm-stat"><span class="cm-stat-label">Team average</span><span class="cm-stat-value">{{ fmtPct(report.avg_score_pct) }}</span></div>
      <div class="cm-stat"><span class="cm-stat-label">Audits this month</span><span class="cm-stat-value">{{ report.audit_count }}</span></div>
      <div class="cm-stat is-ok"><span class="cm-stat-label">Green</span><span class="cm-stat-value">{{ fmtPct(report.green_pct) }}</span></div>
      <div class="cm-stat" style="max-width:360px;"><span class="cm-stat-label">Top missed question</span><span style="font-weight:600;color:#353537;">{{ report.top_missed_question || '—' }}</span></div>
    </div>

    <!-- Loading / empty -->
    <div v-if="loading" data-test="report-skeleton" style="flex: 1; min-height: 0; display: flex; gap: 12px; padding: 2px 16px 16px;">
      <div class="cm-panel" style="width: 340px; flex: 0 0 340px; overflow: hidden;">
        <div style="padding: 11px 14px 9px; border-bottom: 1px solid #E8E9E9;"><div class="h-4 w-28 bg-gray-200 rounded animate-pulse" /></div>
          <div v-for="n in 8" :key="n" style="padding: 12px 14px; border-top: 1px solid #F0F0F1;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px;"><div class="h-3 w-32 bg-gray-200 rounded animate-pulse" /><div class="h-4 w-10 bg-gray-200 rounded animate-pulse" /></div>
            <div class="h-1.5 w-full bg-gray-200 rounded animate-pulse mb-2" />
            <div class="h-2.5 w-16 bg-gray-200 rounded animate-pulse" />
          </div>
      </div>
      <div class="cm-panel" style="flex: 1; padding: 14px;">
        <div class="h-4 w-48 bg-gray-200 rounded animate-pulse mb-4" />
        <div v-for="n in 6" :key="n" style="display: flex; gap: 16px; padding: 10px 0; border-top: 1px solid #F0F0F1;"><div class="h-3 w-20 bg-gray-200 rounded animate-pulse" /><div class="h-3 w-32 bg-gray-200 rounded animate-pulse" /><div class="h-3 w-24 bg-gray-200 rounded animate-pulse" /><div class=" ml-auto w-12 bg-gray-200 rounded animate-pulse" /></div>
      </div>
    </div>

    <div v-else-if="!results.length" style="flex: 1; display: flex; align-items: center; justify-content: center;">
      <div style="text-align: center; max-width: 320px;">
        <div style=" font-weight: 700; color: #353537;">No graded calls yet</div>
        <div style="font-size: 0.86em; color: #A5A5A6; margin-top: 4px; line-height: 1.5;">
          Grading needs a transcript. Check that call recording is flowing for these
          task types.
        </div>
      </div>
    </div>

    <!-- ── three levels ───────────────────────────────────────────────── -->
    <div v-else style="flex: 1; min-height: 0; display: flex; gap: 12px; padding: 2px 16px 16px;">
      <!-- 1. CARE MANAGERS -->
      <div class="cm-panel" style="width: 340px; flex: 0 0 340px; display: flex; flex-direction: column; min-height: 0; overflow: hidden;">
        <div style="display: flex; align-items: baseline; gap: 8px; padding: 11px 14px 9px; border-bottom: 1px solid #E8E9E9;">
          <div style=" font-weight: 700; color: #353537;">Care Managers</div>
          <select
            v-model="sortBy"
            class="qa-sort"
            style="margin-left: auto;"
            aria-label="Sort care managers"
          >
            <option value="score_desc">Highest score first</option>
            <option value="score_asc">Lowest score first</option>
            <option value="flagged_desc">Most flagged first</option>
            <option value="calls_desc">Most calls first</option>
            <option value="name_asc">Name (A–Z)</option>
          </select>
        </div>

        <div style="flex: 1; min-height: 0; overflow-y: auto;">
          <div
            v-for="cm in rankedResults"
            :key="cm.care_manager_user_id"
            class="qa-cm-row"
            :class="{ on: selectedCm === cm.care_manager_user_id }"
            @click="selectCm(cm)"
          >
            <div style="display: flex; align-items: baseline; gap: 8px;">
              <div class="sensitive" style=" font-weight: 600; color: #353537; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                {{ cm.care_manager_name || `User ${cm.care_manager_user_id}` }}
              </div>
              <div
                style="margin-left: auto; font-size: 1.1em; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1;"
                :style="{ color: scoreColour(cm.overall.avg_score_pct) }"
              >
                {{ fmtPct(cm.overall.avg_score_pct) }}
              </div>
            </div>

            <!-- The bar is the ranking, read at a glance; the number is the value. -->
            <div style="height: 3px; border-radius: 2px; background: #F0F0F1; margin: 6px 0 5px; overflow: hidden;">
              <div
                style="height: 100%; border-radius: 2px;"
                :style="{ width: barWidth(cm.overall.avg_score_pct), background: scoreColour(cm.overall.avg_score_pct) }"
              ></div>
            </div>

            <div style="display: flex; align-items: center; gap: 8px; font-size: 0.86em; color: #A5A5A6; font-variant-numeric: tabular-nums;">
              <span>{{ gradedTotal(cm) }} graded</span>
              <span v-if="failedTotal(cm)" style="color: #B91C1C; font-weight: 600;">
                {{ failedTotal(cm) }} flagged
              </span>
              <span v-if="cm.overall.small_sample" class="qa-tag qa-tag-warn" style="margin-left: auto;">small</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 2. CALLS + 3. ONE CALL -->
      <div class="cm-panel" style="flex: 1; min-width: 0; display: flex; flex-direction: column; min-height: 0; overflow: hidden;">
        <div v-if="!activeCm" style="flex: 1; display: flex; align-items: center; justify-content: center;">
          <div style="text-align: center;">
            <div style=" font-weight: 600; color: #6B7280;">Select a care manager</div>
            <div style="font-size: 0.86em; color: #A5A5A6; margin-top: 3px;">
              their graded calls, then the rubric behind any one of them
            </div>
          </div>
        </div>

        <template v-else>
          <!-- Breadcrumb: the two levels below the list, always showing where you are. -->
          <div style="display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-bottom: 1px solid #E8E9E9; flex-wrap: wrap;">
            <div class="qa-crumb sensitive" :class="{ on: !activeCall }" @click="openCall(null)">
              {{ activeCm.care_manager_name || `User ${activeCm.care_manager_user_id}` }}
            </div>
            <template v-if="activeCall">
              <FontAwesomeIcon :icon="['fas', 'angle-right']" style="font-size: 0.86em; color: #D4D4D6;" />
              <div class="qa-crumb on">
                {{ activeCall.rubric_label }}<template v-if="activeCall.program"> · {{ activeCall.program }}</template> · {{ fmtDate(activeCall.engagement_at) }}
              </div>
              <span v-if="callDetail.patient_name" class="sensitive" style="color:#6B7280;" data-test="call-patient">{{ callDetail.patient_name }}</span>
              <span style="margin-left:auto;display:flex;align-items:center;gap:6px;">
                <audio v-if="isRecordingFor(callDetail) && humanRecordingUrl" :src="humanRecordingUrl" controls style="height:28px;" data-test="call-audio" />
                <button
                  v-else-if="callDetail.call_log_uuid" class="qa-back" :disabled="humanRecordingLoading" data-test="call-play-audio"
                  @click="loadRecording(callDetail)"
                >{{ humanRecordingLoading && isRecordingFor(callDetail) ? 'Preparing audio…' : 'Play audio' }}</button>
                <span v-if="recordingError && isRecordingFor(callDetail)" style="color:#B91C1C;" data-test="call-audio-error">{{ recordingError }}</span>
                <router-link v-if="activeCall.work_id" class="qa-back" style="text-decoration:none;" :to="sessionLocation(activeCall.work_id)" target="_blank" data-test="call-open-session">Open session</router-link>
              </span>
            </template>
            <span v-else style="font-size: 0.86em; color: #A5A5A6; font-variant-numeric: tabular-nums;">
              <template v-if="callsLoading">Loading graded calls…</template>
              <template v-else>{{ activeCalls.length }} graded {{ activeCalls.length === 1 ? 'call' : 'calls' }}</template>
              <span v-if="activeCm.overall.small_sample" style="color: #B45309;">
                — small sample, read individual calls rather than the average
              </span>
            </span>
            <button
              v-if="activeCall"
              class="qa-back"
              style="margin-left: auto;"
              @click="openCall(null)"
            >
              <FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 0.86em;" />
              All calls
            </button>
          </div>

          <!-- 2. that CM's calls -->
          <div v-if="!activeCall" class="qa-scroll" style="flex: 1; min-height: 0; overflow: auto;">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50 sticky top-0 z-[1]">
                <tr>
                  <th class="px-3 py-2.5"><span class="sr-only">Session</span></th>
                  <th class="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                  <th class="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Patient</th>
                  <th class="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Rubric</th>
                  <th class="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{{ opportunityNoun }}</th>
                  <th class="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Call type</th>
                  <th class="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Outcome</th>
                  <th class="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Graded by</th>
                  <th class="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Duration</th>
                  <th class="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Points</th>
                  <th class="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Score</th>
                  <th class="px-3 py-2.5"><span class="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <template v-if="callsLoading">
                  <tr v-for="n in 8" :key="`skeleton-${n}`" data-test="calls-skeleton">
                    <td class="px-3 py-2.5"><div class="h-6 w-24 bg-gray-200 rounded-md animate-pulse"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-24 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-32 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-28 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-28 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-28 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-20 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-20 bg-gray-200 rounded animate-pulse"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-10 bg-gray-200 rounded animate-pulse ml-auto"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-10 bg-gray-200 rounded animate-pulse ml-auto"></div></td>
                  <td class="px-3 py-2.5"><div class="h-4 w-12 bg-gray-200 rounded animate-pulse ml-auto"></div></td>
                    <td class="px-3 py-2.5"></td>
                  </tr>
                </template>
                <tr v-else-if="!activeCalls.length">
                  <td colspan="12" class="px-4 py-10 text-center text-sm text-gray-500" data-test="calls-empty">No graded calls{{ reportRubricKey || reportProgram ? ' match these filters' : '' }}.</td>
                </tr>
                <tr
                  v-for="c in activeCalls"
                  v-else
                  :key="c.result_id ? `ai-${c.result_id}` : `audit-${c.analyst_job_id}`"
                  class="qa-call-row hover:bg-gray-50"
                  @click="openCall(c)"
                >
                  <td class="px-3 py-2.5 whitespace-nowrap" @click.stop>
                    <router-link v-if="c.work_id" class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50" style="text-decoration:none;" :to="sessionLocation(c.work_id)" target="_blank" data-test="calls-open-session">Open session</router-link>
                  </td>
                  <td class="px-3 py-2.5 text-sm text-gray-700" style="white-space: nowrap;">{{ fmtDate(c.engagement_at) }}</td>
                  <td class="px-3 py-2.5 text-sm text-gray-700"><span class="sensitive font-medium text-gray-900">{{ c.patient_name || '—' }}</span></td>
                  <td class="px-3 py-2.5 text-sm text-gray-700" style="white-space: nowrap;">
                    {{ c.rubric_label }}
                    <span v-if="c.failed" class="qa-tag qa-tag-fail" style="margin-left: 6px;">auto-fail</span>
                  </td>
                  <td class="px-3 py-2.5 text-sm text-gray-700 qa-td-clip" :title="c.program || ''" data-test="call-program">{{ c.program || '—' }}</td>
                  <td class="px-3 py-2.5 text-sm text-gray-500 qa-td-clip" :title="c.task_type_name || ''">{{ c.task_type_name || '—' }}</td>
                  <td class="px-3 py-2.5 text-sm text-gray-500" style="white-space: nowrap;" data-test="call-outcome">{{ fmtOutcome(c.call_outcome) }}</td>
                  <td class="px-3 py-2.5 text-sm text-gray-500" style="white-space: nowrap;" data-test="call-graded-by"><span :class="{ sensitive: c.source === 'human' }">{{ c.source === 'human' ? (c.auditor_name || 'Auditor') : 'AI' }}</span></td>
                  <td class="px-3 py-2.5 text-sm text-gray-500 text-right" style="font-variant-numeric: tabular-nums;">{{ fmtDuration(c.duration_seconds) }}</td>
                  <td class="px-3 py-2.5 text-sm text-gray-500 text-right" style="font-variant-numeric: tabular-nums;">{{ c.earned }}/{{ c.applicable }}</td>
                  <td class="px-3 py-2.5 text-sm text-right font-bold" style="font-variant-numeric: tabular-nums;" :style="{ color: scoreColour(c.score_pct) }">{{ fmtPct(c.score_pct) }}</td>
                  <td class="px-3 py-2.5"><FontAwesomeIcon :icon="['fas', 'chevron-right']" class="qa-chev" style="font-size: 0.86em;" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 3. one call: the full rubric -->
          <div v-else style="flex: 1; min-height: 0; overflow: auto;">
            <div v-if="callLoading" data-test="call-skeleton">
              <div style="padding: 14px 16px; border-bottom: 1px solid #E8E9E9;">
                <div style="display: flex; gap: 10px; align-items: baseline; margin-bottom: 12px;"><div class="h-7 w-16 bg-gray-200 rounded animate-pulse" /><div class="h-3 w-32 bg-gray-200 rounded animate-pulse" /></div>
                <div class="h-3 w-3/4 bg-gray-200 rounded animate-pulse mb-2" />
                <div class="h-3 w-1/2 bg-gray-200 rounded animate-pulse" />
              </div>
              <div v-for="n in 8" :key="n" style="display: flex; gap: 16px; padding: 12px 16px; border-top: 1px solid #F0F0F1;">
                <div class="h-4 w-10 bg-gray-200 rounded animate-pulse" />
                <div style="flex: 1;"><div class="h-3 w-3/4 bg-gray-200 rounded animate-pulse mb-2" /><div class="h-2.5 w-1/3 bg-gray-200 rounded animate-pulse" /></div>
                <div class="h-8 w-1/3 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>

            <template v-else>
              <QaGradedCall :grade="callDetail" :questions="detailQuestions" />
              <QaAudioConnection v-if="callDetail.call_log_uuid" :session-uuid="callDetail.call_log_uuid" />
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapGetters } from 'vuex';
import { z } from 'zod';
import isEqual from 'lodash/isEqual';
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue';
import { BrowserStore } from '@/utils/BrowserStore';
import { listScorecards, getReport, listRubrics, listAuditCareManagers, listCalls } from '@/services/api/cmQaApi';
import { qaReportLocation } from '../../../router/caremanagement_routes';
import QaAudioConnection from './QaAudioConnection.vue';
import QaGradedCall from './QaGradedCall.vue';
import qaRecordingMixin from './qaRecordingMixin';
import { fmtDate, fmtDuration, fmtPct, sessionLocation } from './qaFormat';

// Saved filters live in this browser (like the CM Patients list); the audit calls page has its own key.
const reportFilterStore = new BrowserStore(
  'cm_qa_report_saved_filters',
  z.object({
    reportRubricKey: z.string().default(''),
    reportProgram: z.string().default(''),
    reportTeam: z.number().nullable().default(null),
    reportCm: z.number().nullable().default(null),
  }),
);

export default {
  name: 'QaReport',
  components: { FilterDropdown, FontAwesomeIcon, QaAudioConnection, QaGradedCall },
  mixins: [qaRecordingMixin],
  props: {
    canAudit: { type: Boolean, default: false },
    cmUserId: { type: Number, default: null },
    workId: { type: Number, default: null },
  },
  data() {
    const saved = reportFilterStore.load();
    return {
      loading: false,
      callLoading: false,
      sortBy: 'score_desc',
      results: [],
      selectedCm: null,
      activeCm: null,
      allCalls: [],
      callsLoading: false,
      activeCall: null,
      callDetail: {},
      report: {},
      reportLoading: false,
      rubrics: [],
      // Everyone with a call in the audit queue and their teams: the team and care manager filters' options.
      careManagers: [],
      teams: [],
      // The dropdowns' options come from the first load (scorecards, report, care managers): skeletons until then.
      reportFiltersReady: false,
      reportRubricKey: saved.reportRubricKey,
      reportProgram: saved.reportProgram,
      reportTeam: saved.reportTeam,
      reportCm: saved.reportCm,
      savedReportFilters: reportFilterStore.hasSavedData() ? saved : null,
    };
  },
  computed: {
    ...mapGetters('tenant', ['opportunityNoun', 'opportunityNounPlural']),
    programOptions() {
      const names = new Set([...(this.report.programs || []), ...(this.reportProgram ? [this.reportProgram] : [])]);
      return [...names].sort().map((name) => ({ value: name, label: name }));
    },
    // The graded call's rubric (sections, order); answers to questions it no longer lists still show.
    detailQuestions() {
      const rubric = this.rubrics.find(r => r.rubric_key === this.callDetail.rubric_key);
      return rubric ? rubric.questions : (this.callDetail.answers || []).map(a => ({ id: a.id, question: this.questionText(a.id) }));
    },
    // The open care manager's graded calls, narrowed by the report's rubric and program filters.
    activeCalls() {
      return this.allCalls.filter((c) => (!this.reportRubricKey || c.rubric_key === this.reportRubricKey)
        && (!this.reportProgram || c.program === this.reportProgram));
    },
    rubricOptions() {
      return this.rubrics.map((r) => ({ value: r.rubric_key, label: r.rubric_label }));
    },
    reportFilterState() {
      return { reportRubricKey: this.reportRubricKey, reportProgram: this.reportProgram, reportTeam: this.reportTeam, reportCm: this.reportCm };
    },
    hasReportFilters() {
      return Object.values(this.reportFilterState).some(Boolean);
    },
    isReportFilterDirty() {
      if (this.savedReportFilters === null) return this.hasReportFilters;
      return !isEqual(this.reportFilterState, this.savedReportFilters);
    },
    teamOptions() {
      return this.teams.map((t) => ({ value: t.id, label: t.name }));
    },
    teamsByUser() {
      return Object.fromEntries(this.careManagers.map((cm) => [cm.care_manager_user_id, cm.team_ids]));
    },
    reportCmOptions() {
      return this.teamScopedResults
        .map((cm) => ({ value: cm.care_manager_user_id, label: cm.care_manager_name || `User ${cm.care_manager_user_id}` }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    teamScopedResults() {
      if (!this.reportTeam) return this.results;
      return this.results.filter((cm) => (this.teamsByUser[cm.care_manager_user_id] || []).includes(this.reportTeam));
    },
    // With a rubric picked, each care manager shows only that rubric: their score is that rubric's, and care
    // managers with no grades on it drop out.
    scopedResults() {
      const people = this.reportCm
        ? this.teamScopedResults.filter((cm) => cm.care_manager_user_id === this.reportCm)
        : this.teamScopedResults;
      if (!this.reportRubricKey) return people;
      return people.flatMap((cm) => {
        const rubric = (cm.rubrics || []).find((r) => r.rubric_key === this.reportRubricKey);
        return rubric ? [{ ...cm, overall: rubric, rubrics: [rubric] }] : [];
      });
    },
    // User-selectable ordering. In EVERY score-based order a null score sorts
    // LAST, never first — an unscored care manager is a data gap, not a top or
    // bottom performer, and must not head the list either way.
    rankedResults() {
      const rows = [...this.scopedResults];
      const score = cm => {
        const s = cm.overall.avg_score_pct;
        return s === null || s === undefined ? null : s;
      };
      const byScore = dir => (a, b) => {
        const x = score(a);
        const y = score(b);
        if (x === null && y === null) return 0;
        if (x === null) return 1;
        if (y === null) return -1;
        return dir * (x - y);
      };
      switch (this.sortBy) {
        case 'score_asc':
          return rows.sort(byScore(1));
        case 'flagged_desc':
          return rows.sort((a, b) => this.failedTotal(b) - this.failedTotal(a));
        case 'calls_desc':
          return rows.sort((a, b) => this.gradedTotal(b) - this.gradedTotal(a));
        case 'name_asc':
          return rows.sort((a, b) => String(a.care_manager_name || '').localeCompare(String(b.care_manager_name || '')));
        case 'score_desc':
        default:
          return rows.sort(byScore(-1));
      }
    },
    totalGraded() {
      return this.scopedResults.reduce((n, cm) => n + this.gradedTotal(cm), 0);
    },
    totalFlagged() {
      return this.scopedResults.reduce((n, cm) => n + this.failedTotal(cm), 0);
    },
    // The per-question TEXT lives on the SAT rubric, not on the answer rows, so
    // the detail view maps id -> question from whatever the API supplied.
    questionMap() {
      const m = {};
      for (const f of this.callDetail.auto_fails || []) m[f.id] = f.question;
      for (const q of this.callDetail.questions || []) m[q.id] = q.question;
      return m;
    },
  },
  watch: {
    cmUserId(val) {
      if (val && this.results.length) {
        const cm = this.scopedResults.find((r) => r.care_manager_user_id === val);
        if (cm && this.selectedCm !== val) this.selectCm(cm, { updateUrl: false });
        this.showCall(this.workId);
      } else if (!val) {
        this.selectedCm = null;
        this.activeCm = null;
        this.activeCall = null;
        this.allCalls = [];
      }
    },
    workId(val) {
      if (this.activeCm) this.showCall(val);
    },
    // The list already holds each call's full grade (answers, patient, recording), so opening one needs no request.
    activeCall(c) {
      if (this.recordingCall) this.resetRecording();
      this.callDetail = c || {};
    },
  },
  async mounted() {
    await Promise.all([this.load(), this.loadCareManagers()]);
    this.reportFiltersReady = true;
  },
  methods: {
    fmtDate,
    fmtDuration,
    fmtPct,
    sessionLocation,
    // call_outcome is a slug (no_answer, voicemail, ...).
    fmtOutcome(v) {
      if (!v) return '—';
      const s = String(v).replace(/_/g, ' ');
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    async load() {
      this.loading = true;
      this.selectedCm = null;
      this.activeCm = null;
      this.activeCall = null;
      // The report's stats fill in when they arrive; the care manager list doesn't wait for them.
      this.loadReport();
      try {
        const [scorecards, rubrics] = await Promise.all([
          listScorecards(this.reportProgram ? { program: this.reportProgram } : {}),
          listRubrics(),
        ]);
        this.results = scorecards.results || [];
        this.rubrics = rubrics.results || [];
      } catch (e) {
        console.error('Failed to load QA scorecards:', e);
        this.results = [];
      } finally {
        this.loading = false;
      }
      if (this.cmUserId && this.results.length) {
        const cm = this.scopedResults.find((r) => r.care_manager_user_id === this.cmUserId);
        if (cm) {
          this.selectCm(cm, { updateUrl: false });
          this.showCall(this.workId);
        }
      }
    },
    async loadCareManagers() {
      if (!this.canAudit) return;
      try {
        const data = await listAuditCareManagers();
        this.careManagers = data.care_managers || [];
        this.teams = data.teams || [];
        // A saved team that no longer exists would filter to nothing unseen.
        if (this.reportTeam && !this.teams.some((t) => t.id === this.reportTeam)) this.reportTeam = null;
      } catch (e) {
        console.error('Failed to load QA care managers:', e);
      }
    },
    async loadReport() {
      this.reportLoading = true;
      const params = this.reportParams();
      try {
        const report = (await getReport(params)) || {};
        // A newer filter change may have started another request; only the latest one fills the stats.
        if (isEqual(params, this.reportParams())) this.report = report;
      } catch (e) {
        console.error('Failed to load QA report:', e);
        this.report = {};
      } finally {
        if (isEqual(params, this.reportParams())) this.reportLoading = false;
      }
    },
    reportParams() {
      const params = {};
      if (this.reportRubricKey) params.rubric_key = this.reportRubricKey;
      if (this.reportProgram) params.program = this.reportProgram;
      return params;
    },
    // The program narrows the care managers' calls too, which the server groups, so it reloads everything.
    setReportProgram(name) {
      this.reportProgram = name || '';
      this.load();
    },
    setReportRubric(key) {
      this.reportRubricKey = key || '';
      this.selectedCm = null;
      this.activeCm = null;
      this.activeCall = null;
      this.loadReport();
    },
    saveReportFilters() {
      const state = { ...this.reportFilterState };
      reportFilterStore.save(state);
      this.savedReportFilters = state;
    },
    clearReportFilters() {
      this.reportTeam = null;
      this.reportCm = null;
      this.reportRubricKey = '';
      this.reportProgram = '';
      this.load();
    },
    // Counts are of every grade (total_*); graded_count/failed_count only cover the calls the average uses.
    gradedTotal(cm) {
      return cm.overall.total_graded ?? cm.overall.graded_count ?? 0;
    },
    failedTotal(cm) {
      return cm.overall.total_failed ?? cm.overall.failed_count ?? 0;
    },
    // Level 1 -> 2. The list is every graded call the care manager has (the scorecard only carries its last few per
    // rubric), filtered by the report's rubric and program filters.
    selectCm(cm, { updateUrl = true } = {}) {
      this.selectedCm = cm.care_manager_user_id;
      this.activeCm = cm;
      this.activeCall = null;
      this.allCalls = [];
      if (updateUrl) {
        this.$router.push(qaReportLocation(cm.care_manager_user_id)).catch(() => {});
      }
      return this.loadCalls(cm.care_manager_user_id);
    },
    async loadCalls(userId) {
      this.callsLoading = true;
      try {
        const data = await listCalls(userId);
        if (this.selectedCm !== userId) return;
        this.allCalls = (data.results || [])
          .sort((a, b) => String(b.engagement_at || '').localeCompare(String(a.engagement_at || '')));
        this.showCall(this.workId);
      } catch (e) {
        console.error('Failed to load graded calls:', e);
        this.allCalls = [];
      } finally {
        if (this.selectedCm === userId) this.callsLoading = false;
      }
    },
    // Level 2 -> 3 and back. The graded call is a place: it gets a URL and a history step.
    openCall(call) {
      if (!this.activeCm) return;
      this.$router.push(qaReportLocation(this.activeCm.care_manager_user_id, call?.work_id)).catch(() => {});
    },
    showCall(workId) {
      this.activeCall = workId ? this.allCalls.find((c) => Number(c.work_id) === workId) || null : null;
    },
    // Coarse bands on purpose: these are samples of a handful of calls, so a
    // fine-grained colour ramp would imply more precision than the data has.
    scoreColour(v) {
      if (v === null || v === undefined) return '#A5A5A6';
      if (v >= 85) return '#0E6B43';
      if (v >= 70) return '#353537';
      return '#B45309';
    },
    barWidth(v) {
      if (v === null || v === undefined) return '0%';
      return `${Math.max(0, Math.min(100, v))}%`;
    },
    questionText(id) {
      const af = (this.callDetail.auto_fails || []).find(f => f.id === id);
      if (af) return af.question;
      return this.questionMap[id] || id;
    },
  },
};
</script>

<style scoped src="./qa.css"></style>
